import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { useModalStyles } from "@/pages/laboratory/styles/modalStyles";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid2";
import { SyntheticEvent, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
	CATALOG_SECTION_INITIAL_VALUES,
	CatalogSection,
} from "../../../models/CatalogSection";
import { upsertCatalogSection } from "../../../repositories/CatalogSectionRepository";

type Props = {
	show: boolean;
	onHide: () => void;
	refreshData: Function;
};
export const CreateSection = ({ show, onHide, refreshData }: Props) => {
	const classes = useModalStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const {
		register,
		control,
		handleSubmit,
		reset,
		clearErrors,
		formState: { errors },
	} = useForm<CatalogSection>({
		defaultValues: CATALOG_SECTION_INITIAL_VALUES,
	});
	const handleClose = (
		event: Event | SyntheticEvent<any, Event>,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};
	const onSubmit: SubmitHandler<CatalogSection> = async (data: any) => {
		setIsSubmitLoading(true);
		const response = await upsertCatalogSection(data);
		if (response.ok) {
			const userId = response.data.tables[0].rows[0].Id;
			setOpen(true);
			onHide();
			refreshData();
		} else {
			setError(response.message);
		}
		setIsSubmitLoading(false);
	};
	useEffect(() => {
		if (show) {
			reset();
			clearErrors();
		}
	}, [show]);
	return (
		<>
			<MesfModal
				open={show}
				handleClose={onHide}
				maxWidth="sm"
				aria-labelledby="contained-modal-title-vcenter"
				title="NEW SECTION"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Grid container spacing={1}>
							<Grid size={{ xs: 12, md: 12 }}>
								<TextField
									{...register("SectionName", { required: true })}
									label="Name"
									variant="outlined"
									error={!!errors.SectionName}
									helperText={errors.SectionName?.message}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="IsCircular"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											checkboxValue={field.value}
											onCheckboxChange={field.onChange}
											label="Is Circular"
											className={classes.checkbox}
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											checkboxValue={field.value}
											onCheckboxChange={field.onChange}
											label="Enabled"
											className={classes.checkbox}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={onHide}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleClose}
				>
					The section was created successfully
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
